import styled from '@emotion/styled';
import { Controller, useFormContext } from 'react-hook-form';
import { Alert, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { BILLING_CYCLE } from '@hotjar/checkout/src/types';
import { BillingCycleOption } from './BillingCycleOption';

const S = {
  Container: styled.div`
    display: flex;
    gap: ${tokens.spacer.x2};
  `,
};

interface BillingCycleSelectorProps {
  monthlyAmounts: IBillingAmounts;
  yearlyAmounts: IBillingAmounts;
  billingCycle?: IBillingCycle;
  onChange: (billingCycle: IBillingCycle) => void;
}

export interface BillingCycleFormField {
  billingCycle: IBillingCycle;
}

export const BillingCycleSelector = ({
  monthlyAmounts,
  yearlyAmounts,
  onChange,
  billingCycle,
}: BillingCycleSelectorProps): JSX.Element => {
  return (
    <S.Container>
      <BillingCycleOption
        id="select-billing-cycle-yearly_recurring"
        name="BillingCycleSelector"
        billingCycle={BILLING_CYCLE.yearly}
        checked={billingCycle === BILLING_CYCLE.yearly}
        onClick={onChange}
        amounts={yearlyAmounts}
      />
      <BillingCycleOption
        id="select-billing-cycle-monthly"
        name="BillingCycleSelector"
        billingCycle={BILLING_CYCLE.monthly}
        checked={billingCycle === BILLING_CYCLE.monthly}
        onClick={onChange}
        amounts={monthlyAmounts}
      />
    </S.Container>
  );
};

/**
 * use this component if you are using the billing cycle selector with react-hook-form
 * it handles the error handling in here too
 */
export const BillingCycleSelectorWithProvider = ({
  monthlyAmounts,
  yearlyAmounts,
  onChange,
  billingCycle,
}: BillingCycleSelectorProps): JSX.Element => {
  const {
    formState: { errors },
    control,
  } = useFormContext<BillingCycleFormField>();
  const { t } = useTranslationWithPrefix('billing:payment-details.form');

  return (
    <>
      <Controller
        control={control}
        name="billingCycle"
        defaultValue={billingCycle}
        rules={{ required: t<string>('error-billing-cycle') }}
        render={({ onChange: onControllerChange, value }) => (
          <BillingCycleSelector
            yearlyAmounts={yearlyAmounts}
            monthlyAmounts={monthlyAmounts}
            billingCycle={value}
            onChange={(newValue) => {
              onControllerChange(newValue);
              onChange(newValue);
            }}
          />
        )}
      />
      {errors.billingCycle && <Alert kind="danger">{errors.billingCycle?.message as string}</Alert>}
    </>
  );
};
